/**
 * Flagship Stadium V5 subsystem (2026-07-22 integration; anime-stadium export
 * swapped in 2026-08-12).
 *
 * Drop-in replacement for the procedural `createStadiumSystem()` at the SAME mount
 * point (subsystems.ts): a `RenderSubsystem` + `CrowdControl` that loads
 * runtime-handoff-v5 and mounts it under ONE `StadiumV5Root` in the shell's
 * scene. It changes ONLY the environment — the sim, physics, players, ball, bots,
 * camera, HUD, rules and netcode are untouched.
 *
 * FIRST VERTICAL SLICE scope (playability first, per the directive §4):
 *   - Mounts the bowl + dressing: core (includes tunnels), roof, seats, props
 *     (includes dugouts), scoreboard, exterior + crowd + the 92 very-big flags.
 *     Keeps the flagship's own pitch/goals/collision/goal-net/ad-boards for
 *     gameplay this pass (the V5 pitch/goals/collision swap is the next pass,
 *     §16) — the excluded GLBs still load (byte-tracked) but are deliberately
 *     never added to the scene, because vfx.ts and sideline.ts already own the
 *     live goal net and ad-board ring UNCONDITIONALLY (see V5_MOUNT_KEYS).
 *   - The bowl is mounted under a +90° yaw: it is authored long-axis-on-Z and
 *     the sim scores on ±X. See V5_BOWL_YAW.
 *   - Crowd is BILLBOARDS, not meshes — 32 distinct baked figures, two triangles
 *     each, all motion in the vertex shader. The instanced 3D crowd it replaced
 *     was one man cloned 85k times at 59.6M triangles; see CrowdBillboardSystem.
 *     Pitchside staff went with it (same single source figure, 6.9 MB download).
 *   - The bowl is re-shaded to the Blender CEL look after mounting (toonLook.ts).
 *   - Lighting/bloom come from the flagship's existing lighting + post subsystems;
 *     this system adds NO second sun. Preset/wetness hooks are present for next pass.
 *   - Every GLTFLoader here shares the client's singleton DRACOLoader
 *     (`castDecoders.ts`) — a fresh one per model would mean a duplicate worker
 *     pool + WASM instantiation for nothing.
 *
 * Async load never blocks the match: onPitchConfig kicks it off; the bowl streams
 * in. Fails loud (console.error) — never a silent V4 fallback — EXCEPT the
 * decorative very-big flags, whose own failure is caught and logged rather than
 * taking the whole bowl down with it.
 */
import { LinearMipmapLinearFilter, NearestFilter, Object3D, SRGBColorSpace, TextureLoader, type Scene, type Texture } from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import type { MatchConfig } from '@fg/shared/sim';
import type { CrowdControl } from './stadium.js';
import type { RenderContext, RenderState, QualitySettings } from './context.js';
import { StadiumV5Loader, type StadiumV5Loaded } from './stadium-v5/StadiumV5Loader.js';
import { CrowdBillboardSystem, parseCrowdBillboards } from './stadium-v5/CrowdBillboardSystem.js';
import { StaffSystem } from './stadium-v5/StaffSystem.js';
import { FlagsSystem, parseFlagVeryBigPlacements } from './stadium-v5/FlagsSystem.js';
import { ScoreboardSystem } from './stadium-v5/ScoreboardSystem.js';
import { applyToonLook } from './stadium-v5/toonLook.js';
import { partitionColliders, hideCollidersForProduction, type SceneNodeLike } from './stadium-v5/collision.js';
import { getDracoLoader } from './player/castDecoders.js';

const V5_BASE = '/assets/stadium-v5';
/**
 * Bowl + dressing modules mounted this pass.
 *
 * `pitch` and `goals` ARE mounted: measured against the shipped geometry they
 * land exactly on the sim — the pitch spans world x±52.5 / z±34 (the 105×68
 * playing area) and the goals reach x±54.5, i.e. `halfLength + GOAL_DEPTH`. The
 * procedural equivalents are switched OFF in subsystems.ts rather than drawn
 * underneath, because two surfaces at y=0 z-fight across the whole field.
 * Physics is untouched by any of this: the sim is analytic (rules/ball-out.ts
 * tests coordinates against pitchLength/pitchWidth) and never reads a mesh, so
 * there is still exactly one physics world.
 *
 * Excluded, and why:
 *   - `collision` — the sim needs no collision mesh; the module is still parsed
 *     so its colliders can be hidden, never added to the scene.
 *   - `goalnets` — kept OUT so vfx.ts's verlet net survives: it billows off the
 *     ball, and these are static 16-triangle alpha panels. Swap only if the
 *     modelled look is worth losing that.
 *   - `ads` — sideline.ts already builds its own ad-board ring off the
 *     touchline/goal-line offsets (AD_SIDE_OFF/AD_END_OFF); a second static ring
 *     from this export duplicates it. Revisit when the ads are redone.
 */
export const V5_MOUNT_KEYS = new Set([
  'core', 'roof', 'seats', 'props', 'scoreboard', 'exterior', 'pitch', 'goals', 'ads',
]);

/**
 * The bowl is modelled with its LONG axis on Z; the sim's is on X.
 *
 * Not a guess — the export's own collision module says so: `PitchBoundary` is
 * x[-34,34] × z[-52.5,52.5] (68 wide on X, 105 long on Z) and the goals sit at
 * z=±54.5, while `rules/ball-out.ts` scores a goal on `b.px` crossing
 * ±halfLength and puts the touchlines on ±`b.pz`. Left uncorrected the whole
 * bowl reads 90° out — goals off the side, dugouts behind a net.
 *
 * Rotating the ROOT (not each module) is what keeps crowd/staff/flag placements
 * — which are all in bowl space and parented here — consistent with the shell.
 * +90° about Y maps bowl +Z onto world +X, which also lands the tunnel and both
 * dugouts on the -Z touchline near halfway, where they belong.
 */
export const V5_BOWL_YAW = Math.PI / 2;

/**
 * Crowd density fraction from the flagship crowd budget (quality.crowdInstances).
 *
 * The old thresholds (0.35 / 0.7 / 1) were sized for the INSTANCED MESH crowd,
 * where every spectator cost 700 triangles and thinning was the only way to hold
 * frame rate. The billboard crowd costs TWO triangles each — the entire 31,635
 * is ~63k triangles, less than a single player model — so thinning it buys
 * nothing and costs a visibly half-empty stadium.
 *
 * Only the explicit "no crowd" budget still empties the bowl; the lowest tier
 * keeps most of it, because a football stadium with a third of a crowd in it
 * reads as broken rather than as a performance setting.
 */
export function crowdDensityFor(crowdInstances: number): number {
  if (crowdInstances <= 0) return 0;
  if (crowdInstances < 4000) return 0.8; // LOW — still a full-looking bowl
  return 1;
}

export class StadiumV5System implements CrowdControl {
  readonly name = 'stadium-v5';
  readonly root = new Object3D();
  private readonly crowd = new CrowdBillboardSystem();
  private readonly staff = new StaffSystem();
  private readonly flags = new FlagsSystem();
  private readonly scoreboard = new ScoreboardSystem();
  private scene: Scene | null = null;
  private loaded: StadiumV5Loaded | null = null;
  private loading = false;
  private version = 'runtime-handoff-v5';
  private density = 1;
  private crowdEnabled = true;
  private wetness = 0;
  /** False on sub-full pitches — the bowl's goals sit at the 105m goal line only. */
  private mountGoals = true;

  constructor() {
    this.root.name = 'StadiumV5Root';
    this.root.rotation.y = V5_BOWL_YAW;
    this.root.add(this.crowd.root, this.staff.root, this.flags.root);
  }

  // ── RenderSubsystem ─────────────────────────────────────────────────────────
  init(ctx: RenderContext): void {
    if (this.scene) return;
    this.scene = ctx.scene;
    ctx.scene.add(this.root);
  }

  /** The shell calls this with the match config; kick off the V5.1 load. */
  onPitchConfig(_config: MatchConfig, ctx: RenderContext): void {
    this.init(ctx);
    // The bowl's modelled goals only fit the FULL pitch (goal line ±52.5).
    // Smaller presets (§9.2) put the sim's goals inboard, so the bowl's
    // goals would stand beyond them as a second, wrong pair — vfx.ts draws
    // the frames at the sim goal line instead (same 104m threshold there).
    // ⚠ NEVER. The bowl's goals and vfx's goal are within 6 cm of each other
    // (measured: bowl mouth 52.44, sim goal line 52.50), so mounting both drew
    // one inside the other - the "doubled goal" the owner photographed. ONE
    // source wins everywhere, and it is vfx's, because that one is built from
    // the SIM's goal line and is therefore correct at every pitch preset and
    // identical in every mode (owner: "make sure it look same in all modes").
    // The bowl's goals are geometry we simply do not need.
    this.mountGoals = false;
    this.density = crowdDensityFor(ctx.quality.crowdInstances);
    void this.load();
  }

  /** Lifecycle load (directive §2) — idempotent; safe to call once per match. */
  async load(): Promise<void> {
    if (this.loading || this.loaded) return;
    this.loading = true;
    const gltf = new GLTFLoader();
    // Every V5 bowl/pitch GLB except the pitch itself ships Draco-compressed
    // (README: "Draco is on everywhere except the pitch" — it buys nothing on
    // 2,946 tris and crashed the encoder there). Reuse the shared singleton
    // (castDecoders.ts) — constructing a fresh DRACOLoader per model spins up a
    // duplicate worker pool + WASM instantiation for nothing.
    gltf.setDRACOLoader(getDracoLoader());
    gltf.setMeshoptDecoder(MeshoptDecoder);
    const loadGlb = (url: string, onBytes?: (l: number, t: number) => void): Promise<{ scene: Object3D }> =>
      new Promise((resolve, reject) => {
        gltf.load(url, (g) => resolve({ scene: g.scene }), (e) => onBytes?.(e.loaded, e.total),
          (err) => reject(err instanceof Error ? err : new Error(String(err))));
      });
    const loadJson = async (url: string): Promise<unknown> => {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`${res.status} ${url}`);
      return res.json();
    };

    console.info('[Flagship Stadium] Loading runtime-handoff-v5.1');
    const loader = new StadiumV5Loader(V5_BASE, loadGlb, loadJson);
    let loaded: StadiumV5Loaded;
    try {
      loaded = await loader.load({
        onCanEnter: () => console.info('[Flagship Stadium] Gameplay-critical assets ready'),
        log: () => { /* final line emitted below with the flagship prefix */ },
      });
    } catch (e) {
      // FAIL LOUD (§5) — never fall back to V4.
      console.error('[Flagship Stadium] FAILED to load runtime-handoff-v5.1 (NO V4 fallback):', e);
      this.loading = false;
      return;
    }
    this.loaded = loaded;
    this.version = loaded.version;

    // Mount ONLY the bowl modules; the flagship keeps its pitch/goals/collision.
    for (const [key, obj] of loaded.modules) {
      if (key === 'goals' && !this.mountGoals) continue; // wrong place on small pitches — see onPitchConfig
      if (V5_MOUNT_KEYS.has(key)) {
        // Loaded meshes default to receiveShadow=false, and the procedural
        // ground this replaces had it ON — without this the players stand on the
        // pitch casting nothing, which reads as them floating. Only the goals
        // CAST: the bowl is 72k triangles of mostly-offscreen structure, and the
        // sun's shadow camera is framed on the pitch, so casting from the stands
        // costs a re-render of all of it for shadows nobody can see.
        const casts = key === 'goals';
        obj.traverse((n) => {
          const mesh = n as { isMesh?: boolean; receiveShadow?: boolean; castShadow?: boolean };
          if (!mesh.isMesh) return;
          mesh.receiveShadow = true;
          mesh.castShadow = casts;
        });
        this.root.add(obj);
      } else if (key === 'collision') {
        const p = partitionColliders(obj as unknown as SceneNodeLike);
        hideCollidersForProduction(p.colliders);
      }
    }

    await this.wireToonLook();
    await this.wireCrowd(loaded);
    await this.wireFlags(loaded);
    // LED boards go live off the real match state. AFTER the toon pass, which
    // deliberately leaves emissive/scoreboard materials untouched (toonLook.ts
    // KEEP_ORIGINAL) — this binds to that preserved emissive material.
    const sbModule = loaded.modules.get('scoreboard');
    if (sbModule) this.scoreboard.bind(sbModule);
    console.info(`[Flagship Stadium] Loaded ${this.version}`);
    console.info(`[Flagship Stadium] visible bowl modules: ${[...loaded.modules.keys()].filter((k) => V5_MOUNT_KEYS.has(k)).join(', ')}`);
    this.loading = false;
  }

  /**
   * Re-shade the mounted bowl to the Blender CEL look (see toonLook.ts for why
   * the node group itself cannot be ported).
   *
   * ORDER IS LOAD-BEARING: after mounting, so it catches every module — but
   * BEFORE the crowd and flags are built, so it never touches them. Both own
   * custom shaders (the flags' wave and the crowd's billboarding live in
   * `onBeforeCompile`/`ShaderMaterial`), and swapping those for a stock
   * MeshToonMaterial would silently stop the flags waving and the crowd facing
   * the camera. Their figures are cel-baked already.
   *
   * Non-fatal: a bowl that failed to fetch the ramp still renders, just in PBR.
   */
  private async wireToonLook(): Promise<void> {
    try {
      const gradient = await new Promise<Texture>((resolve, reject) => {
        new TextureLoader().load(`${V5_BASE}/textures/toon_gradient.png`, resolve, undefined,
          () => reject(new Error('toon_gradient.png failed to load')));
      });
      const stats = applyToonLook(this.root, gradient);
      console.info(`[Flagship Stadium] cel look: ${stats.converted} materials → toon, ` +
        `${stats.skippedEmissive} left emissive`);
    } catch (e) {
      console.error('[Flagship Stadium] cel look NOT applied (bowl stays PBR):', e);
    }
  }

  /**
   * The BILLBOARD crowd (see CrowdBillboardSystem's header for why it replaced
   * the instanced 3D one): 32 distinct baked figures against one atlas, two
   * triangles each. Fetched outside the byte-tracked plan — the .bin is raw
   * instance data, not a shape `loadJson`/`loadGlb` can carry — and non-blocking:
   * a bowl with no crowd still plays.
   */
  private async wireCrowd(_loaded: StadiumV5Loaded): Promise<void> {
    try {
      const [meta, bin, atlas] = await Promise.all([
        fetch(`${V5_BASE}/data/crowd_billboard_placements.json`).then((r) => {
          if (!r.ok) throw new Error(`${r.status} crowd_billboard_placements.json`);
          return r.json() as Promise<unknown>;
        }),
        fetch(`${V5_BASE}/data/crowd_billboard_placements.bin`).then((r) => {
          if (!r.ok) throw new Error(`${r.status} crowd_billboard_placements.bin`);
          return r.arrayBuffer();
        }),
        new Promise<Texture>((resolve, reject) => {
          new TextureLoader().load(`${V5_BASE}/textures/crowd_atlas.png`, resolve, undefined,
            () => reject(new Error('crowd_atlas.png failed to load')));
        }),
      ]);
      // NEAREST on MAGnification keeps the baked cel figures crisp up close.
      //
      // MINification must mipmap, and this is not a preference. A far stand is
      // ~1 screen pixel per spectator, so an unmipmapped sprite samples a SINGLE
      // texel of a 2560x2048 atlas — land on one of the transparent gaps between
      // figures and the fragment hits `discard` and the whole person disappears.
      // The far half of the bowl dissolved into empty seats exactly this way.
      // (vfx.ts's net hit the same trap from the other side: see its
      // "there is no net" note, where mipping erased thin threads. The escape
      // there was thicker threads; here it is a lower cutoff, below.)
      atlas.magFilter = NearestFilter;
      atlas.minFilter = LinearMipmapLinearFilter;
      atlas.generateMipmaps = true;
      atlas.anisotropy = 4; // stands are viewed at a heavy grazing angle
      atlas.colorSpace = SRGBColorSpace;

      const placements = parseCrowdBillboards(meta, bin);
      this.crowd.build(placements, atlas);
      this.crowd.setDensity(this.density);
      this.crowd.setEnabled(this.crowdEnabled);
      console.info(`[Flagship Stadium] crowd ${placements.count} billboards @ 2t → ` +
        `${((placements.count * 2) / 1e3).toFixed(0)}k tris, 1 draw, ${placements.poseCount} distinct figures`);
    } catch (e) {
      console.error('[Flagship Stadium] crowd failed to wire:', e);
    }
    // Pitchside STAFF are deliberately not built. They were instanced from the
    // same single `crowd_static_01.glb` figure as the old mesh crowd — 28 copies
    // of one man — and keeping them alive would mean downloading that 6.9 MB GLB
    // for 28 people. The bowl now ships without it entirely.
  }

  /**
   * The very-big flags (§16, wireFlags) are pre-placed at load time and
   * pre-instanced — `stadium_flags_verybig.glb`'s 4 source meshes only, one
   * InstancedMesh per club-colour variant, so 92 placements is one draw call per
   * colour and NO per-instance fetch or transform. Not gameplay-blocking:
   * `wireFlags` catches and logs its own failures rather than aborting the load.
   */
  private async wireFlags(loaded: StadiumV5Loaded): Promise<void> {
    const sourceMeshes = loaded.modules.get('flagsVeryBig');
    if (!sourceMeshes) {
      console.warn('[Flagship Stadium] flagsVeryBig module missing — skipping very-big flags');
      return;
    }
    try {
      const [meta, bin] = await Promise.all([
        fetch(`${V5_BASE}/data/flag_verybig_placements.json`).then((r) => {
          if (!r.ok) throw new Error(`${r.status} flag_verybig_placements.json`);
          return r.json() as Promise<unknown>;
        }),
        fetch(`${V5_BASE}/data/flag_verybig_placements.bin`).then((r) => {
          if (!r.ok) throw new Error(`${r.status} flag_verybig_placements.bin`);
          return r.arrayBuffer();
        }),
      ]);
      const placements = parseFlagVeryBigPlacements(meta, bin);
      this.flags.build(sourceMeshes, placements);
      console.info(`[Flagship Stadium] flags_verybig: ${placements.instances.length} instances, ${placements.variants.length} variants`);
    } catch (e) {
      // Decorative, not gameplay-critical — log and carry on with a flagless bowl
      // rather than taking down core/pitch/crowd over a missing placement file.
      console.error('[Flagship Stadium] flags_verybig failed to wire:', e);
    }
  }

  // three frustum-culls each per-section InstancedMesh automatically, so the
  // per-frame cost here is two uniform writes: one drives the flag wave, one the
  // whole crowd's motion. No per-instance CPU work at any point.
  update(state: RenderState, _ctx: RenderContext): void {
    this.flags.update(state.elapsed);
    this.crowd.update(state.elapsed, state.dt);
    this.scoreboard.update(state); // repaints only when a value changes
  }

  setQuality(quality: QualitySettings, _ctx: RenderContext): void {
    this.density = crowdDensityFor(quality.crowdInstances);
    this.crowd.setDensity(this.density);
  }

  // ── CrowdControl + the directive's lifecycle hooks ──────────────────────────
  setExcitement(v: number): void { this.crowd.setExcitement(v); }
  /** Localized surge — sim/world coords; the crowd's sectors are keyed the same. */
  exciteAt(x: number, z: number, strength: number): void { this.crowd.excite(x, z, strength); }
  setCrowdEnabled(on: boolean): void { this.crowdEnabled = on; this.crowd.setEnabled(on); }
  setWetness(w: number): void { this.wetness = Math.max(0, Math.min(1, w)); /* pitch wetness — next pass */ }
  setPreset(_name: string): void { /* lighting presets — next pass (flagship lighting owns this now) */ }
  getVersion(): string { return this.version; }
  getWetness(): number { return this.wetness; }
  /** How many spectators are actually placed (billboards, 2 triangles each). */
  getCrowdCount(): number { return this.crowd.getCount(); }

  dispose(): void {
    this.crowd.dispose();
    this.staff.dispose();
    this.flags.dispose();
    this.scoreboard.dispose();
    this.scene?.remove(this.root);
    this.root.clear();
    this.loaded = null;
    this.loading = false;
  }
}

/** Factory mirroring `createStadiumSystem()` so subsystems.ts swaps one line. */
export function createStadiumV5System(): StadiumV5System {
  return new StadiumV5System();
}
